import { useState } from "react";
import { generatePath, Navigate, useNavigate } from "react-router-dom";
import { useBoolean } from "usehooks-ts";

import { ScreenLayout } from "@/app/components/layouts";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import {
  RefillRequirementsAlert,
  SymptomsForm,
  VideoConsultationFormSection,
} from "@/features/consultations/components";
import { useConsultationStore } from "@/features/consultations/contexts/use-consultation-store";
import { WhiteCoatSyncModal } from "@/features/intake-form/components/white-coat-sync-modal";
import { CATEGORY_SLUG, SUBSCRIPTION_VENDOR } from "@/shared/domains";
import { FollowUpFormHeader, FollowUpInjectionsForm } from "./sections";
import type { FollowUpFormStep } from "./utils";
import {
  FOLLOW_UP_FORM_STEPS,
  getFollowUpFormHeaderDescription,
} from "./utils";

export const ConsultationFollowUpForm = () => {
  const navigate = useNavigate();
  const appointmentType = useConsultationStore(
    (state) => state.appointmentType,
  );

  const [step, setStep] = useState<FollowUpFormStep>(
    FOLLOW_UP_FORM_STEPS.SYMPTOMS_FORM,
  );

  const { value: showWhiteCoatModal, setTrue: openWhiteCoatModal } =
    useBoolean();

  const canAccessActiveWLFeatures = usePortalUserStore(
    (state) => state.canAccessActiveWLFeatures,
  );

  const eligibleSubscription = usePortalUserStore(({ portalUser }) =>
    portalUser?.subscriptions.find(
      (subscription) =>
        subscription.product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
        subscription.product.category.slug === CATEGORY_SLUG.MICRODOSING,
    ),
  );

  if (!eligibleSubscription || !canAccessActiveWLFeatures) {
    return <Navigate to={ROUTES.BASE} replace />;
  }

  const handleSubmit = () => {
    if (eligibleSubscription.vendor === SUBSCRIPTION_VENDOR.WHITE_COAT) {
      return openWhiteCoatModal();
    }

    const search = appointmentType ? `?appointmentType=${appointmentType}` : "";
    return void navigate(generatePath(ROUTES.CONSULTATIONS.SCHEDULE) + search);
  };

  return (
    <ScreenLayout className="gap-0">
      <FollowUpFormHeader
        description={getFollowUpFormHeaderDescription(step)}
      />

      {step === FOLLOW_UP_FORM_STEPS.SYMPTOMS_FORM && (
        <SymptomsForm
          onSubmit={() => setStep(FOLLOW_UP_FORM_STEPS.INJECTIONS_FORM)}
        />
      )}

      {step === FOLLOW_UP_FORM_STEPS.INJECTIONS_FORM && (
        <FollowUpInjectionsForm
          onCancel={() => setStep(FOLLOW_UP_FORM_STEPS.SYMPTOMS_FORM)}
          onSubmit={() => setStep(FOLLOW_UP_FORM_STEPS.HEALTHIE_FORM)}
        />
      )}

      {step === FOLLOW_UP_FORM_STEPS.HEALTHIE_FORM && (
        <div className="flex flex-col gap-6">
          <RefillRequirementsAlert />

          <VideoConsultationFormSection
            subscription={eligibleSubscription}
            onCancel={() => navigate(ROUTES.CONSULTATIONS.PURPOSE)}
            onSubmit={handleSubmit}
          />
        </div>
      )}

      <WhiteCoatSyncModal open={showWhiteCoatModal} />
    </ScreenLayout>
  );
};
